import { ProductCategory } from "@/types/productTypes";
import fetchDb from "../utils/customRequestFunction";
import { AdminSliceCreator } from "./useAdminStore";

export interface CategoryStoreState {
   showCategories: boolean;
   toggleShowCategories: () => void;
   categories: ProductCategory[];
   loading: boolean;
   error: string | null;

   fetchCategories: () => Promise<void>;
   createCategory: (data: { name: string }) => Promise<ProductCategory>;
   updateCategory: (
      id: number,
      data: Partial<ProductCategory>
   ) => Promise<ProductCategory>;
   deleteCategory: (id: number) => Promise<void>;
   reorderCategories: (id1: number, id2: number) => Promise<void>;
   assignProduct: (productId: number, categoryId: number) => Promise<void>;
   removeProduct: (productId: number) => Promise<void>;
   assignImageToCategory: (
      categoryId: number,
      imageId: number
   ) => Promise<void>;
}

export const categoryStoreSlice: AdminSliceCreator<CategoryStoreState> = (
   set,
   get
) => ({
   showCategories: false,
   toggleShowCategories: () => set({ showCategories: !get().showCategories }),
   categories: [],
   loading: false,
   error: null,

   fetchCategories: async () => {
      set({ loading: true, error: null });
      try {
         const response = await fetchDb.get("/api/product-category");
         set({ categories: response, loading: false });
      } catch (error) {
         console.error("Error fetching categories:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to fetch categories",
            loading: false,
         });
      }
   },

   createCategory: async (data: { name: string }) => {
      set({ loading: true, error: null });
      try {
         const response = await fetchDb.post("/api/product-category", data);
         set((state) => ({
            categories: [...state.categories, response],
            loading: false,
         }));
         return response;
      } catch (error) {
         console.error("Error creating category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to create category",
            loading: false,
         });
         throw error;
      }
   },

   updateCategory: async (id: number, data: Partial<ProductCategory>) => {
      set({ loading: true, error: null });
      try {
         const response = await fetchDb.patch(
            `/api/product-category/${id}`,
            data
         );
         set((state) => ({
            categories: state.categories.map((category) =>
               category.id === id ? response : category
            ),
            loading: false,
         }));
         return response;
      } catch (error) {
         console.error("Error updating category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to update category",
            loading: false,
         });
         throw error;
      }
   },

   deleteCategory: async (id: number) => {
      set({ loading: true, error: null });
      try {
         await fetchDb.delete(`/api/product-category/${id}`);
         set((state) => ({
            categories: state.categories.filter(
               (category) => category.id !== id
            ),
            loading: false,
         }));
      } catch (error) {
         console.error("Error deleting category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to delete category",
            loading: false,
         });
         throw error;
      }
   },

   reorderCategories: async (id1: number, id2: number) => {
      set({ loading: true, error: null });
      try {
         await fetchDb.patch(`/api/product-category/reorder/${id1}/${id2}`);
         await get().fetchCategories(); // Refresh categories to get the new order
      } catch (error) {
         console.error("Error reordering categories:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to reorder categories",
            loading: false,
         });
         throw error;
      }
   },

   assignProduct: async (productId: number, categoryId: number) => {
      set({ loading: true, error: null });
      try {
         await fetchDb.patch(
            `/api/main-product/${productId}/category/${categoryId}`
         );
         await get().fetchCategories(); // Refresh to update product assignments
         set({ loading: false });
      } catch (error) {
         console.error("Error assigning product to category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to assign product",
            loading: false,
         });
         throw error;
      }
   },

   removeProduct: async (productId: number) => {
      set({ loading: true, error: null });
      try {
         await fetchDb.patch(`/api/main-product/${productId}/category/null`);
         await get().fetchCategories(); // Refresh to update product assignments
         set({ loading: false });
      } catch (error) {
         console.error("Error removing product from category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to remove product",
            loading: false,
         });
         throw error;
      }
   },

   assignImageToCategory: async (categoryId: number, imageId: number) => {
      set({ loading: true, error: null });
      try {
         const response = await fetchDb.patch(
            `/api/product-category/${categoryId}`,
            { imageId }
         );
         set((state) => ({
            categories: state.categories.map((category) =>
               category.id === categoryId
                  ? {
                       ...category,
                       imageId,
                       image: get().allImages.find((img) => img.id === imageId),
                    }
                  : category
            ),
            loading: false,
         }));
         return response;
      } catch (error) {
         console.error("Error assigning image to category:", error);
         set({
            error:
               error instanceof Error
                  ? error.message
                  : "Failed to assign image to category",
            loading: false,
         });
         throw error;
      }
   },
});
